import { useCallback, useEffect, useState } from 'react';
import { Mail, Send } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Spinner } from '@/components/atoms/Spinner';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/context/ToastContext';
import { cn } from '@/utils/cn';
import {
  listBackofficeEmails,
  previewBackofficeEmail,
  sendBackofficeTestEmail,
  type BackofficeEmailInfo,
  type BackofficeEmailPreview,
} from '@/services/api/emails.api';
import styles from './EmailsPage.module.scss';

/**
 * Backoffice · Emails.
 * Lista los emails transaccionales del sistema, muestra una vista previa del
 * HTML y permite enviarse pruebas de cada uno para revisar que son correctos.
 */
export function EmailsPage() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [emails, setEmails] = useState<BackofficeEmailInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preview, setPreview] = useState<BackofficeEmailPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [recipient, setRecipient] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [sendingId, setSendingId] = useState<string | null>(null);

  // Precargar el destinatario con el email del admin logueado.
  useEffect(() => {
    if (user?.email) setRecipient((cur) => cur || user.email);
  }, [user?.email]);

  // Cargar el catálogo de emails.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    listBackofficeEmails()
      .then((list) => {
        if (cancelled) return;
        setEmails(list);
        setError(null);
        if (list.length > 0) setSelectedId((cur) => cur ?? list[0]!.id);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error cargando los emails');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Cargar la vista previa del email seleccionado.
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreview(null);
    previewBackofficeEmail(selectedId)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const handleSend = useCallback(
    async (id: string) => {
      const to = recipient.trim();
      if (!to) {
        toast({ description: 'Introduce un destinatario.', variant: 'default' });
        return;
      }
      setSendingId(id);
      try {
        const res = await sendBackofficeTestEmail(id, to, recipientName.trim() || undefined);
        toast({
          title: 'Email enviado',
          description: `Enviado a ${res.to}`,
          variant: 'success',
        });
      } catch (err) {
        toast({
          title: 'No se pudo enviar',
          description: err instanceof Error ? err.message : 'Error enviando el email',
          variant: 'default',
        });
      } finally {
        setSendingId(null);
      }
    },
    [recipient, recipientName, toast]
  );

  const selected = emails.find((e) => e.id === selectedId) ?? null;

  return (
    <div className={styles.page}>
      <header className={styles.page__header}>
        <h1 className={styles.page__title}>Emails</h1>
        <p className={styles.page__subtitle}>
          Revisa los emails del sistema, envíate pruebas y manda a mano los que no son
          automáticos, como la invitación.
        </p>
      </header>

      <div className={styles.recipient}>
        <div className={styles.recipient__field}>
          <label htmlFor="email-recipient" className={styles.recipient__label}>
            Destinatario
          </label>
          <Input
            id="email-recipient"
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="tu@email.com"
            fullWidth
          />
        </div>
        <div className={styles.recipient__field}>
          <label htmlFor="email-recipient-name" className={styles.recipient__label}>
            Nombre del destinatario
          </label>
          <Input
            id="email-recipient-name"
            type="text"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            placeholder="Ana"
            fullWidth
            aria-describedby="email-recipient-name-hint"
          />
          <span id="email-recipient-name-hint" className={styles.recipient__hint}>
            Personaliza el saludo. Si lo dejas vacío se usa tu nombre.
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.center}>
          <Spinner />
        </div>
      ) : error ? (
        <div className={styles.center}>
          <p className={styles.error}>{error}</p>
        </div>
      ) : emails.length === 0 ? (
        <div className={styles.center}>
          <p>No hay emails configurados.</p>
        </div>
      ) : (
        <div className={styles.layout}>
          <ul className={styles.list}>
            {emails.map((email) => (
              <li key={email.id} className={styles.item}>
                <button
                  type="button"
                  className={cn(styles.card, email.id === selectedId && styles['card--active'])}
                  onClick={() => setSelectedId(email.id)}
                  aria-pressed={email.id === selectedId}
                >
                  <span className={styles.card__icon}>
                    <Mail size={18} aria-hidden />
                  </span>
                  <span className={styles.card__body}>
                    <span className={styles.card__title}>{email.label}</span>
                    <span className={styles.card__desc}>{email.description}</span>
                    <span className={styles.card__trigger}>{email.trigger}</span>
                  </span>
                </button>
                <div className={styles.item__actions}>
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<Send size={15} aria-hidden />}
                    isLoading={sendingId === email.id}
                    onClick={() => void handleSend(email.id)}
                  >
                    Enviar
                  </Button>
                </div>
              </li>
            ))}
          </ul>

          <div className={styles.preview}>
            <div className={styles.preview__bar}>
              <span className={styles.preview__name}>{selected?.label ?? 'Vista previa'}</span>
              {preview?.subject && (
                <span className={styles.preview__subject}>Asunto: {preview.subject}</span>
              )}
            </div>
            <div className={styles.preview__frameWrap}>
              {previewLoading ? (
                <div className={styles.center}>
                  <Spinner />
                </div>
              ) : preview ? (
                <iframe
                  title="Vista previa del email"
                  className={styles.preview__frame}
                  srcDoc={preview.html}
                  sandbox=""
                />
              ) : (
                <div className={styles.center}>
                  <p>Selecciona un email para previsualizarlo.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmailsPage;
